<template>
  <div class="form-wrapper">
    <!-- Titel des Formulars -->
    <h2>Login</h2>
    <form @submit.prevent="handleLogin">
      <div class="form-group">
        <label for="email">E-Mail:</label>
        <input type="email" id="email" v-model="email" required />
      </div>
      <div class="form-group">
        <label for="password">Passwort:</label>
        <input type="password" id="password" v-model="password" required />
      </div>
      <!-- Der Button ist während des Ladevorgangs deaktiviert -->
      <button type="submit" :disabled="loading">{{ loading ? 'Prüfung dauert...' : 'Einloggen' }}</button>
      <!-- Link zur "Passwort vergessen"-Seite -->
      <router-link to="/reset" class="reset-link">Passwort nicht mehr da?</router-link>
    </form>
    <!-- Trennlinie für alternative Anmeldemethoden -->
    <div class="divider">
      <span>oder</span>
    </div>
    <a :href="discordLoginUrl" class="discord-button">
      <svg class="discord-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 127.14 96.36" aria-hidden="true">
        <path fill="currentColor" d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0 105.89 105.89 0 0 0 19.39 8.09C2.79 32.65-1.71 56.6.54 80.21h0A105.73 105.73 0 0 0 32.71 96.36a77.7 77.7 0 0 0 6.89-11.28 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2.05a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2.05a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.27A105.25 105.25 0 0 0 126.6 80.22c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 59.98 31 52.94S36.06 40.2 42.45 40.2c6.44 0 11.63 5.76 11.52 12.74 0 7.04-5.08 12.75-11.52 12.75Zm42.24 0c-6.27 0-11.45-5.71-11.45-12.75S78.3 40.2 84.69 40.2c6.44 0 11.62 5.76 11.52 12.74 0 7.04-5.08 12.75-11.52 12.75Z"/>
      </svg>
      <span>Mit Discord anmelden</span>
    </a>
    <!-- Anzeige von Fehlermeldungen -->
    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import {getDiscordURL, login} from '../services/api';

// Reaktive Zustandsvariablen für die Formular-Eingaben und den UI-Zustand.
const email = ref('');
const password = ref('');
const errorMessage = ref('');
const router = useRouter();

const loading = ref(false);

// Prüft, ob ein Discord-Code in der URL vorhanden ist (nach der Rückkehr von Discord).
// Zeigt in diesem Fall einen Ladezustand an.
const params = new URLSearchParams(window.location.search);
if (params.has("code")) {
  loading.value = true;
  setTimeout(()=>{
    loading.value = false;
  }, 3000);
}

/**
 * Eine einfache clientseitige Validierung für E-Mail-Adressen.
 * @param {string} email - Die zu prüfende E-Mail.
 * @returns {boolean}
 */
const isValidEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Handler für das Absenden des Login-Formulars.
 */
const handleLogin = async () => {
  errorMessage.value = '';
  loading.value = true;
  if (!isValidEmail(email.value)) {
    errorMessage.value = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    return;
  }
  try {
    // Ruft die Login-Funktion aus dem API-Service auf.
    await login(email.value, password.value);
    // Leitet bei Erfolg zur Startseite weiter.
    router.push('/');
  } catch (error) {
    // Zeigt bei einem Fehler die Fehlermeldung an.
    errorMessage.value = error.message;
  } finally {
    loading.value = false;
  }
};

// Holt die dynamische Discord-Login-URL.
const discordLoginUrl = getDiscordURL();
</script>

<style lang="scss" scoped>
.form-wrapper {
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  background: $card-background;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  h2 {
    margin-top: 0;
    color: $secondary-color;
    text-align: center;
    margin-bottom: 2rem;
  }

  .form-group {
    margin-bottom: 1.5rem;
    text-align: left;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: bold;
      color: $text-color;
    }

    input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid $border-color;
      border-radius: 4px;
      font-size: 1rem;
      box-sizing: border-box;

      &:focus {
        outline: none;
        border-color: $primary-color;
        box-shadow: 0 0 0 3px rgba($primary-color, 0.2);
      }
    }
  }

  button {
    width: 100%;
    padding: 0.75rem;
    border: none;
    border-radius: 4px;
    background-color: $primary-color;
    color: white;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: $primary-color-hover;
    }
  }

  .error {
    color: red;
    margin-top: 1rem;
    text-align: center;
  }
}
.divider {
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: #888;
  font-size: 0.9rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: $border-color;
  }

  span {
    padding: 0 1rem;
  }
}

.discord-button {
  width: 100%;
  padding: 0.75rem;

  border: none;
  border-radius: 4px;

  background-color: #5865F2;
  color: white;

  font-size: 1rem;
  font-weight: bold;
  text-decoration: none;

  cursor: pointer;

  transition:
    background-color 0.2s,
    transform 0.15s;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;

  box-sizing: border-box;

  &:hover {
    background-color: #4752C4;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}
.discord-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
.reset-link {
  display: block;
  margin-top: 1rem;
  text-align: center;
  color: $primary-color;
  font-size: 0.9rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>